import { z } from 'zod';
import { isoDateSchema, todayIso } from './dates';
import { PROGRESS_EXCLUDED_TASK_STATUSES } from './tasks.schema';

export const PROJECT_DOMAINS = ['Website', 'Marketing', 'Automation'] as const;
export const projectDomainSchema = z.enum(PROJECT_DOMAINS);
export type ProjectDomain = z.infer<typeof projectDomainSchema>;

/**
 * The list tabs (Figma A05.1: Active / Completed / Archived) over `projects.status`.
 * TODO(kb): there is no 'Archived' status in the DB enum; Cancelled stands in for it until product decides otherwise.
 */
export const PROJECT_BUCKETS = ['active', 'completed', 'archived'] as const;
export const projectBucketSchema = z.enum(PROJECT_BUCKETS);
export type ProjectBucket = z.infer<typeof projectBucketSchema>;

/** The project statuses (a subset of the shared status enum) that each bucket covers. */
export const PROJECT_BUCKET_STATUSES = {
  active: ['Pending', 'In Progress', 'On Hold'],
  completed: ['Completed'],
  archived: ['Cancelled'],
} as const satisfies Record<ProjectBucket, readonly string[]>;

export const PROJECT_NAME_MAX = 255;
export const PROJECT_DESCRIPTION_MAX = 2000;

/**
 * Body of POST /api/projects (admin or the business's account manager). Shared so the web "New project"
 * drawer validates exactly what the API does. Dates are calendar dates, not instants.
 */
export const createProjectInputSchema = z
  .object({
    /** The client's id: one business per client, so the business id is the user id. */
    businessId: z.string().min(1),
    name: z.string().trim().min(1, 'Enter a project name').max(PROJECT_NAME_MAX, 'Keep the name under 255 characters'),
    domain: projectDomainSchema,
    description: z
      .string()
      .trim()
      .max(PROJECT_DESCRIPTION_MAX, 'Keep the description under 2000 characters')
      .default(''),
    startDate: isoDateSchema.optional(),
    targetEndDate: isoDateSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.targetEndDate && value.targetEndDate < todayIso()) {
      ctx.addIssue({ code: 'custom', path: ['targetEndDate'], message: 'Pick today or a later date' });
    }
    if (value.startDate && value.targetEndDate && value.targetEndDate < value.startDate) {
      ctx.addIssue({ code: 'custom', path: ['targetEndDate'], message: 'The end date comes before the start' });
    }
  });

export type CreateProjectInput = z.input<typeof createProjectInputSchema>;

export const PROJECT_LIST_LIMIT_MAX = 50;
export const PROJECT_LIST_LIMIT_DEFAULT = 20;

/** Query of GET /api/projects. Staff must name a business; clients always get their own (it is ignored for them). */
export const listProjectsQuerySchema = z.object({
  businessId: z.string().min(1).optional(),
  bucket: projectBucketSchema.default('active'),
  domain: projectDomainSchema.optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().max(300).optional(),
  limit: z.coerce.number().int().min(1).max(PROJECT_LIST_LIMIT_MAX).default(PROJECT_LIST_LIMIT_DEFAULT),
});

/** What the API receives after validation (defaults applied, `limit` a number). */
export type ListProjectsParams = z.output<typeof listProjectsQuerySchema>;

/**
 * Project progress is a stored rollup: the average of its tasks' progress, 0 when it has none. Cancelled tasks and
 * drafts (status "User Draft", not real work yet) do not count. The API recomputes it in SQL whenever a task is
 * created, changes or is submitted; this is the same rule in code, for tests and for places that must show a figure
 * before the write lands. Never derive it on read.
 * TODO(kb): the KB says "avg task progress"; excluding Cancelled and draft tasks is a judgement call to confirm.
 */
export function projectProgressFromTasks(tasks: readonly { progress: number; status?: string }[]): number {
  const excluded: readonly string[] = PROGRESS_EXCLUDED_TASK_STATUSES;
  const counted = tasks.filter((t) => !t.status || !excluded.includes(t.status));
  if (counted.length === 0) return 0;
  const total = counted.reduce((sum, t) => sum + Math.min(100, Math.max(0, t.progress)), 0);
  return Math.round(total / counted.length);
}

export const projectSchema = z.object({
  id: z.string(),
  businessId: z.string().nullable(),
  businessName: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  domain: projectDomainSchema,
  /** The raw status; the UI maps it to a label. */
  status: z.string(),
  /** Stored rollup of task progress, 0-100. */
  progress: z.number().int().min(0).max(100),
  startDate: z.string(),
  targetEndDate: z.string().nullable(),
  /** Tasks in this project as the caller may see them (clients: client-visible tasks only). */
  taskCount: z.number().int(),
  /** Of those, how many are Completed. */
  doneTaskCount: z.number().int(),
  /**
   * What needs a person's attention, for the line under the task count (Figma A05.1). Staff views only: for a client
   * these are 0, since "needs triage" and the like are the agency's own wording.
   */
  waitingForClientCount: z.number().int(),
  changesRequestedCount: z.number().int(),
  needsTriageCount: z.number().int(),
  /** Up to three people working open tasks here (the avatar stack). Empty for a client. */
  team: z.array(z.string()),
  /** The open task due soonest (undated ones last), for "Next: Diwali offer reel · Sun 5 Oct" on a client's project card. */
  nextTask: z.object({ title: z.string(), deadline: z.string().nullable() }).nullable(),
  /** The business's primary account manager (the longest-serving one when none is marked primary). */
  accountManagerName: z.string().nullable(),
  updatedAt: z.string(),
});

export type ProjectResponse = z.infer<typeof projectSchema>;

export const projectListSchema = z.object({
  items: z.array(projectSchema),
  nextCursor: z.string().nullable(),
  /** Set when the list is for one business (staff view); null otherwise. TODO(api): replaced by the businesses API. */
  business: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** How many projects each tab holds (Active 4 · Completed 1 · Archived 0), whatever the current search. */
  counts: z.object({ active: z.number().int(), completed: z.number().int(), archived: z.number().int() }),
});

export type ProjectListResponse = z.infer<typeof projectListSchema>;
